import {Router} from "@angular/router";
import {Component, inject} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";

import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";

import {appRoutes} from "@numizmat/constants";
import {AlertService} from "@numizmat/services/common";
import {ForceRepaintDirective} from "@numizmat/directives";
import {LoginRequest} from "@numizmat/services-models/account";
import {AccessTokenService, CurrentUserStateService, LoginApiService} from "@numizmat/services/account";

@Component({
  selector: 'nz-login-modal',
  imports: [
    ReactiveFormsModule,
    ForceRepaintDirective
  ],
  templateUrl: './login-modal.component.html',
  styleUrl: './login-modal.component.scss'
})
export class LoginModalComponent {
  private router: Router = inject(Router);
  private activeModal: NgbActiveModal = inject(NgbActiveModal);
  private alertService: AlertService = inject(AlertService);
  private loginApiService: LoginApiService = inject(LoginApiService);
  private accessTokenService: AccessTokenService = inject(AccessTokenService);
  private currentUserStateService: CurrentUserStateService = inject(CurrentUserStateService);

  protected loading: boolean = false;
  protected errorMessage: string = '';
  protected form: FormGroup = this.initializeForm();

  protected readonly appRoutes = appRoutes;

  protected onSubmit(): void {
    this.loading = true;
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.loading = false;
      return;
    }
    const formValue = this.form.getRawValue();
    const request: LoginRequest = {
      emailOrUsername: formValue.emailOrUsername,
      password: formValue.password
    };
    this.loginApiService.login(request).subscribe({
      next: response => {
        this.loading = false;
        if (response.success) {
          this.accessTokenService.setAccessToken(response.accessToken);
          this.currentUserStateService.setCurrentUser({
            loggedIn: true,
            userData: {
              userId: response.userId,
              username: response.username,
              newMessageCount: response.newMessageCount,
              newNotificationCount: response.newNotificationCount,
              confirmationMethod: response.confirmationMethod,
              confirmationMethodName: response.confirmationMethodName
            }
          });
          this.alertService.showLoginSuccess();
          this.currentUserStateService.setLoginState(true);
          this.closeModal();
        } else {
          this.errorMessage = response.errorMessage;
        }
      }
    });
  }

  protected closeModal(route?: string[]): void {
    this.activeModal.dismiss();

    if (!!route) {
      setTimeout(() => {
        this.router.navigate(route).then();
      }, 100);
    }
  }

  private initializeForm() {
    return new FormGroup({
      emailOrUsername: new FormControl(null, [Validators.required]),
      password: new FormControl(null, [Validators.required])
    });
  }

  get emailOrUsername() {
    return this.form.get('emailOrUsername');
  }

  get password() {
    return this.form.get('password');
  }
}
